import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Users, UserPlus, X, Building, Edit2 } from 'lucide-react';

// Import extracted components
import { UserTable, UserFormModal, ClientUserSummary } from '../components/users';

const API = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const { user: currentUser, isAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'employee',
    assigned_client_ids: []
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/users`);
      setUsers(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(`Failed to fetch users: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/clients`);
      setClients(response.data);
    } catch (err) {
      toast.error("Failed to fetch clients");
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin()) {
      fetchUsers();
      fetchClients();
    }
  }, [authLoading, isAdmin, fetchUsers, fetchClients]);

  const handleCreateUser = async () => {
    if (!formData.email || !formData.full_name) {
      toast.error('Email and name are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/users`, formData);
      setUsers([...users, response.data]);
      setTempPassword(response.data.temp_password);
      setShowCreateModal(false);
      resetForm();
      toast.success('User created successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await axios.put(`${API}/api/users/${selectedUser.id}`, {
        full_name: formData.full_name,
        role: formData.role,
        assigned_client_ids: formData.assigned_client_ids,
        is_active: formData.is_active
      });
      
      setUsers(users.map(u => u.id === selectedUser.id ? response.data : u));
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      toast.success('User updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/api/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axios.post(`${API}/api/users/${userId}/reset-password`);
      setTempPassword(response.data.temp_password);
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      assigned_client_ids: user.assigned_client_ids || [],
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      role: 'employee',
      assigned_client_ids: []
    });
  };

  const handleViewClients = (user) => {
    setViewingUser(user);
    setShowClientsModal(true);
  };

  if (!isAdmin()) {
    return (
      <div className="p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <p className="text-red-700">You don't have permission to access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6" />
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage users and their client access</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Temp Password Alert */}
      {tempPassword && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-yellow-800">Temporary Password Generated</p>
                <p className="text-yellow-700">
                  Share this with the user: <code className="bg-yellow-100 px-2 py-1 rounded font-mono">{tempPassword}</code>
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setTempPassword(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client-User Assignment Summary */}
      <ClientUserSummary clients={clients} users={users} />

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No users found. Create your first user to get started.</p>
            </div>
          ) : (
            <UserTable
              users={users}
              clients={clients}
              currentUserId={currentUser?.id}
              onEdit={openEditModal}
              onDelete={handleDeleteUser}
              onResetPassword={handleResetPassword}
              onViewClients={handleViewClients}
            />
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <UserFormModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        mode="create"
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleCreateUser}
        clients={clients}
      />

      {/* Edit User Modal */}
      <UserFormModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
          resetForm();
        }}
        mode="edit"
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleUpdateUser}
        clients={clients}
      />

      {/* View Assigned Clients Modal */}
      <Dialog open={showClientsModal} onOpenChange={setShowClientsModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Assigned Clients - {viewingUser?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {viewingUser?.assigned_client_ids?.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {viewingUser.assigned_client_ids.map((clientId, index) => {
                  const client = clients.find(c => c.id === clientId);
                  return (
                    <div 
                      key={clientId} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                      data-testid={`assigned-client-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Building className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{client?.name || 'Unknown Client'}</p>
                          {client?.pan && (
                            <p className="text-xs text-slate-500">PAN: {client.pan}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {client?.short_name || clientId.slice(0, 8)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Building className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No clients assigned to this user</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientsModal(false)}>Close</Button>
            <Button onClick={() => {
              setShowClientsModal(false);
              openEditModal(viewingUser);
            }}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
